import { forwardRef, Children, cloneElement, useState, useEffect, useRef, useMemo } from 'react';
import type {
  Ref,
  PropsWithoutRef,
  ReactElement,
  ChangeEvent,
  MouseEvent as ReactMouseEvent
} from 'react';
import styled, { css, type DefaultTheme } from 'styled-components';

import type { RefElement, TestIdProp, WithAttributes } from '../../types';
import FormField from '../FormField';
import type { FormFieldProps } from '../FormField';
import { StyledFormField, StyledFormFieldInfo } from '../FormField/FormField';
import type { FormControlProps } from '../FormControl';
import type { RadioCheckProps } from '../RadioCheck';
import Flex from '../Flex';
import { defaultThemeProp } from '../../theme';
import { useArrows, useConsolidatedRef, useElement, useTestIds, useUID } from '../../hooks';
import {
  StyledImageContainer,
  StyledSelectionCard,
  StyledSelectionCardInline
} from '../SelectionCard/SelectionCard.styles';
import { getActiveElement, withTestIds } from '../../utils';
import type { elements } from '../FormField/FormField.test-ids';
import { StyledRadioCheck } from '../RadioCheck/RadioCheck';

import { getRadioCheckGroupTestIds } from './RadioCheckGroup.test-ids';

export type RadioCheckGroupProps = WithAttributes<
  'fieldset',
  TestIdProp<typeof elements> & {
    /** Accepts Checkboxes or RadioButtons as children. */
    children: ReactElement | ReactElement[];
    /**
     * Conveniently pass an onChange handler to the group to be notified when any of the inputs change.
     * Additionally, onChange can be passed to individual children.
     * Both handlers will be called, child followed by group.
     */
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    /** Callback invoked when the clear button is clicked. If provided will render the clear button. */
    onClear?: FormFieldProps['onClear'];
    /** Set visual state based on a validation state. */
    status?: FormControlProps['status'];
    /** Pass a string or a fragment with an Icon and string. */
    label?: FormControlProps['label'];
    /** Visually hides the label region. */
    labelHidden?: FormControlProps['labelHidden'];
    /** It is recommended to pass a simple string to offer guidance. Text will be styled based on status prop. */
    info?: FormControlProps['info'];
    /** Indicate if the field is required. The browser defaults to false. */
    required?: FormControlProps['required'];
    /** Disable the control. The browser defaults to false. */
    disabled?: FormControlProps['disabled'];
    /** Makes the input non editable and non clickable. The browser defaults to false. */
    readOnly?: FormControlProps['readOnly'];
    /**
     * Layout field elements inline in a row.
     * @default false
     */
    inline?: FormFieldProps['inline'];
    /** Used to toggle the auto stacking feature of an inlined group.
     * @default true
     */
    autoStack?: boolean;
    /** Sets html name attribute for the underlying control. Useful for mapping to a data field. */
    name?: FormControlProps['name'];
    /**
     * Controls the styling of the child RadioChecks.
     * @default 'simple'
     */
    variant?: RadioCheckProps['variant'] | 'card-grid';
    /** Pass a heading and content to show additional information on the field. */
    additionalInfo?: FormControlProps['additionalInfo'];
    /**
     * Enables arrow key navigation between options in the group.
     * Disable for checkbox groups where each option is independently selectable.
     * @default true
     */
    arrowNavigation?: boolean;
    /** Ref forwarded to the wrapping element. */
    ref?: Ref<HTMLElement>;
  }
>;

export const StyledRadioCheckGroup = styled.fieldset(() => {
  return css`
    container-type: inline-size;
    flex-wrap: nowrap;

    &[disabled] {
      > ${StyledFormField} {
        opacity: unset;
      }
    }

    ${StyledSelectionCard} {
      max-width: 100%;
    }

    ${StyledSelectionCardInline} ${StyledImageContainer} {
      /* To enlarge the image containers to the size of the biggest one */
      min-height: 100%;
    }

    ${StyledRadioCheck} > ${StyledFormFieldInfo} {
      display: none;
    }
  `;
});

const getCardGridContainerStyles = (theme: DefaultTheme) => {
  const cardWidth = theme.base['content-width'].md;
  return css`
    display: grid;
    gap: ${theme.base.spacing};
    grid-template-columns: 1fr;
    grid-auto-rows: 1fr;

    @container (min-width: calc(2 * ${cardWidth})) {
      grid-template-columns: repeat(2, 1fr);
    }

    @container (min-width: calc(3 * ${cardWidth})) {
      grid-template-columns: repeat(3, 1fr);
    }

    @container (min-width: calc(4 * ${cardWidth})) {
      grid-template-columns: repeat(4, 1fr);
    }
  `;
};

const StyledCardGrid = styled.div<{
  renderInline: boolean;
  autoStack: boolean;
  childCount: number;
}>(({ theme, renderInline, autoStack, childCount }) => {
  if (!renderInline) {
    return css`
      display: grid;
      gap: ${theme.base.spacing};
      grid-template-columns: minmax(min-content, max-content);
      grid-auto-rows: 1fr;
    `;
  }

  return autoStack
    ? getCardGridContainerStyles(theme)
    : css`
        display: grid;
        gap: ${theme.base.spacing};
        grid-template-columns: repeat(${childCount}, 1fr);
        grid-auto-rows: 1fr;
      `;
});

StyledCardGrid.defaultProps = defaultThemeProp;

const RadioCheckGroup = forwardRef<
  RefElement<RadioCheckGroupProps>,
  PropsWithoutRef<RadioCheckGroupProps>
>(function RadioCheckGroup(props, ref) {
  const uid = useUID();
  const {
    testId,
    children,
    name = uid,
    disabled = false,
    required = false,
    onChange,
    readOnly,
    inline = false,
    autoStack = true,
    variant = 'simple',
    arrowNavigation = true,
    onClear,
    ...restProps
  } = props;

  const testIds = useTestIds(testId, getRadioCheckGroupTestIds);
  const [optionsEl, setOptionsEl] = useElement<HTMLDivElement>();
  const [renderInline, setRenderInline] = useState(inline);
  const renderInlineRef = useRef(inline);
  const minInlineWidth = useRef(0);
  const containerRef = useConsolidatedRef(ref);
  const skipRef = useRef<HTMLElement>(null);

  useArrows(
    arrowNavigation ? containerRef : skipRef,
    {
      selector: '[data-main-focus]',
      cycle: false,
      dir: 'both'
    },
    [children]
  );

  useEffect(() => {
    setRenderInline(inline);
    if (inline && optionsEl && optionsEl.lastChild && autoStack) {
      const intersectionObserver = new IntersectionObserver(
        entries => {
          if (entries[0].intersectionRatio < 1) {
            if (entries[0].rootBounds) minInlineWidth.current = entries[0].rootBounds.width + 1;
            renderInlineRef.current = false;
            setRenderInline(false);
          }
        },
        { root: optionsEl, threshold: 1 }
      );

      const resizeObserver = new ResizeObserver(entries => {
        if (!renderInlineRef.current && entries[0].contentRect.width > minInlineWidth.current) {
          renderInlineRef.current = true;
          setRenderInline(true);
        }
      });

      intersectionObserver.observe(optionsEl.lastChild as Element);
      resizeObserver.observe(optionsEl);

      return () => {
        intersectionObserver.disconnect();
        resizeObserver.disconnect();
      };
    }
  }, [optionsEl, inline, autoStack]);

  const mapChildren = useMemo(
    () =>
      Children.map(children, child =>
        cloneElement(child, {
          name,
          status: restProps.status,
          disabled: disabled || child.props.disabled,
          readOnly: readOnly || child.props.readOnly,
          variant: variant === 'card-grid' ? 'card' : variant,
          suppressAnnouncements: true,
          onChange: onChange
            ? (e: ChangeEvent<HTMLInputElement>) => {
                if (!(readOnly || child.props.readOnly)) {
                  child.props.onChange?.(e);
                  onChange(e);
                }
              }
            : child.props.onChange
        })
      ),
    [children, name, disabled, readOnly, variant, onChange]
  );

  return (
    <FormField
      testId={testIds}
      as={StyledRadioCheckGroup}
      ref={containerRef}
      name={name}
      disabled={disabled}
      required={required}
      {...restProps}
      inline={false}
      isRadioCheck
      onClear={onClear}
      // Prevents blur when any input within the group is actively focused.
      onMouseDown={(e: ReactMouseEvent<HTMLFieldSetElement>) => {
        restProps.onMouseDown?.(e);

        if (!containerRef.current) return;

        if (!containerRef.current.contains(getActiveElement())) return;

        if (!(e.target instanceof Element) || !e.target.closest('label')) return;

        e.preventDefault();
      }}
    >
      {variant === 'card-grid' ? (
        <StyledCardGrid
          ref={setOptionsEl}
          renderInline={renderInline}
          autoStack={autoStack}
          childCount={Children.count(children)}
        >
          {mapChildren}
        </StyledCardGrid>
      ) : (
        <Flex
          ref={setOptionsEl}
          container={{
            direction: renderInline ? 'row' : 'column',
            colGap: renderInline ? 1.5 : 2,
            rowGap: variant === 'card' ? 1 : 0,
            wrap: 'wrap'
          }}
        >
          {mapChildren}
        </Flex>
      )}
    </FormField>
  );
});

export default withTestIds(RadioCheckGroup, getRadioCheckGroupTestIds);
